export const TOUR_TOOLTIP_DEFAULT_WIDTH = 320;
export const TOUR_TOOLTIP_DEFAULT_HEIGHT = 220;
export const TOUR_TOOLTIP_OFFSET = 12;
export const TOUR_VIEWPORT_PADDING = 8;

export type TourTooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface TourTooltipRect {
  top: number;
  left: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
}

export interface TourTooltipCoords {
  top: number;
  left: number;
  placement: TourTooltipPlacement;
}

function coordsForPlacement(
  rect: TourTooltipRect,
  placement: TourTooltipPlacement,
  cardWidth: number,
  cardHeight: number,
): { top: number; left: number } {
  switch (placement) {
    case 'top':
      return {
        top: rect.top - cardHeight - TOUR_TOOLTIP_OFFSET,
        left: rect.left + rect.width / 2 - cardWidth / 2,
      };
    case 'bottom':
      return {
        top: rect.bottom + TOUR_TOOLTIP_OFFSET,
        left: rect.left + rect.width / 2 - cardWidth / 2,
      };
    case 'left':
      return {
        top: rect.top + rect.height / 2 - cardHeight / 2,
        left: rect.left - cardWidth - TOUR_TOOLTIP_OFFSET,
      };
    case 'right':
      return {
        top: rect.top + rect.height / 2 - cardHeight / 2,
        left: rect.right + TOUR_TOOLTIP_OFFSET,
      };
  }
}

function fitsInViewport(
  top: number,
  left: number,
  cardWidth: number,
  cardHeight: number,
  viewportWidth: number,
  viewportHeight: number,
): boolean {
  return (
    top >= TOUR_VIEWPORT_PADDING &&
    left >= TOUR_VIEWPORT_PADDING &&
    top + cardHeight <= viewportHeight - TOUR_VIEWPORT_PADDING &&
    left + cardWidth <= viewportWidth - TOUR_VIEWPORT_PADDING
  );
}

function clampToViewport(
  top: number,
  left: number,
  cardWidth: number,
  cardHeight: number,
  viewportWidth: number,
  viewportHeight: number,
): { top: number; left: number } {
  return {
    top: Math.max(
      TOUR_VIEWPORT_PADDING,
      Math.min(top, viewportHeight - cardHeight - TOUR_VIEWPORT_PADDING),
    ),
    left: Math.max(
      TOUR_VIEWPORT_PADDING,
      Math.min(left, viewportWidth - cardWidth - TOUR_VIEWPORT_PADDING),
    ),
  };
}

const PLACEMENT_FALLBACK_ORDER: Record<TourTooltipPlacement, TourTooltipPlacement[]> = {
  bottom: ['bottom', 'top', 'right', 'left'],
  top: ['top', 'bottom', 'right', 'left'],
  left: ['left', 'right', 'bottom', 'top'],
  right: ['right', 'left', 'bottom', 'top'],
};

/** Pick a viewport-safe tooltip position, flipping when the preferred side has no room. */
export function computeTourTooltipPosition(
  rect: TourTooltipRect,
  preferred: TourTooltipPlacement | undefined,
  cardWidth: number,
  cardHeight: number,
  viewportWidth: number,
  viewportHeight: number,
): TourTooltipCoords {
  const pref = preferred ?? 'bottom';
  const order = PLACEMENT_FALLBACK_ORDER[pref];

  for (const placement of order) {
    const { top, left } = coordsForPlacement(rect, placement, cardWidth, cardHeight);
    if (fitsInViewport(top, left, cardWidth, cardHeight, viewportWidth, viewportHeight)) {
      return { top, left, placement };
    }
  }

  const spaceBelow = viewportHeight - rect.bottom - TOUR_VIEWPORT_PADDING;
  const spaceAbove = rect.top - TOUR_VIEWPORT_PADDING;
  const useBelow = spaceBelow >= spaceAbove;
  const fallbackPlacement = useBelow ? 'bottom' : 'top';
  const raw = coordsForPlacement(rect, fallbackPlacement, cardWidth, cardHeight);
  const clamped = clampToViewport(
    raw.top,
    raw.left,
    cardWidth,
    cardHeight,
    viewportWidth,
    viewportHeight,
  );

  return { ...clamped, placement: fallbackPlacement };
}
